import { useState } from 'react';
import { Settings, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth, WorkspaceWithRole } from '../contexts/AuthContext';

interface WorkspaceSwitcherProps {
  onNavigate?: (path: string) => void;
}

export const WorkspaceSwitcher = ({ onNavigate }: WorkspaceSwitcherProps) => {
  const { currentWorkspace, availableWorkspaces, workspaceLoading, switchWorkspace } = useAuth();
  const { t } = useTranslation('shared');

  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const handleSwitchWorkspace = async (workspaceId: string) => {
    if (switching || currentWorkspace?.id === workspaceId) return;

    setSwitching(true);
    try {
      await switchWorkspace(workspaceId);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to switch workspace:', error);
    } finally {
      setSwitching(false);
    }
  };

  const getRoleColor = (role: WorkspaceWithRole['role']) => {
    switch (role) {
      case 'owner':
        return 'text-purple-600';
      case 'member':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const getRoleLabel = (role: WorkspaceWithRole['role']) => {
    return t(`workspaceSwitcher.role.${role}`, {
      defaultValue: role.charAt(0).toUpperCase() + role.slice(1),
    });
  };

  if (workspaceLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-8 h-8 bg-gray-200 rounded animate-pulse" />
        <div className="w-24 h-4 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (!currentWorkspace) {
    return <div className="text-sm text-gray-500">{t('workspaceSwitcher.none')}</div>;
  }

  return (
    <div className="relative">
      {/* Current Workspace Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
      >
        {/* Workspace Avatar */}
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
          {currentWorkspace.name.charAt(0).toUpperCase()}
        </div>

        {/* Workspace Info */}
        <div className="flex flex-col items-start">
          <span className="font-medium text-gray-900 text-sm">{currentWorkspace.name}</span>
          <span className="text-xs text-gray-500">{currentWorkspace.code}</span>
        </div>

        {/* Dropdown Arrow */}
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transform transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          {/* Dropdown Content */}
          <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border z-20">
            <div className="p-2">
              {/* Selected Workspace Header */}
              <div className="px-3 py-2 mb-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {t('workspaceSwitcher.selectedHeading')}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpen(false);
                      if (onNavigate) onNavigate('/workspace/settings');
                    }}
                    className="p-1 rounded hover:bg-gray-200 transition-colors"
                    title={t('workspaceSwitcher.settingsTooltip')}
                  >
                    <Settings className="w-4 h-4 text-gray-500" />
                  </button>
                </div>

                {/* Current Workspace Display */}
                <div className="flex items-center space-x-3 p-2 bg-blue-50 rounded-lg">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                    {currentWorkspace.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-blue-700 truncate">
                      {currentWorkspace.name}
                    </div>
                    <div className="flex items-center space-x-2 text-xs">
                      <span className="text-blue-600 truncate">{currentWorkspace.code}</span>
                      <span className="text-blue-400">•</span>
                      <span className={getRoleColor(currentWorkspace.role)}>
                        {getRoleLabel(currentWorkspace.role)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Other Workspaces */}
              {availableWorkspaces.filter((w) => w.id !== currentWorkspace.id).length > 0 && (
                <>
                  <div className="border-t border-gray-200 mb-2" />
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">
                    {t('workspaceSwitcher.switchHeading')}
                  </div>
                  <div className="space-y-1">
                    {availableWorkspaces
                      .filter((w) => w.id !== currentWorkspace.id)
                      .map((workspace) => (
                        <button
                          key={workspace.id}
                          onClick={() => handleSwitchWorkspace(workspace.id)}
                          disabled={switching}
                          className="w-full flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-100 disabled:opacity-50"
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm bg-gradient-to-br from-gray-400 to-gray-500">
                            {workspace.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <div className="font-medium text-sm truncate">{workspace.name}</div>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <span className="truncate">{workspace.code}</span>
                              <span>•</span>
                              <span className={getRoleColor(workspace.role)}>
                                {getRoleLabel(workspace.role)}
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>
                </>
              )}

              {/* Create New Workspace */}
              <div className="border-t border-gray-200 my-2" />
              <button
                onClick={() => {
                  setIsOpen(false);
                  if (onNavigate) onNavigate('/workspace/create');
                }}
                disabled={switching}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center text-white">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <span className="font-medium text-sm text-gray-700">
                    {t('workspaceSwitcher.createTitle')}
                  </span>
                  <div className="text-xs text-gray-500">
                    {t('workspaceSwitcher.createSubtitle')}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Loading Overlay */}
      {switching && (
        <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};
